import React, { useState } from 'react';
import { useAccountingReport } from './useAccountingReport';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import DetalleCuentaModal from './DetalleCuentaModal';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';

function EstadoResultados({ month, year }) {
    const { userData } = useAuth();

    const { startDate, endDate } = React.useMemo(() => ({
        startDate: moment().year(year).month(month).startOf('month').toDate(),
        endDate: moment().year(year).month(month).endOf('month').toDate()
    }), [year, month]);

    const { loading, accounts, balances, ledgerEntries } = useAccountingReport(
        userData?.condominioId,
        startDate,
        endDate,
        true
    );

    // Modal State
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedEntries, setSelectedEntries] = useState([]);

    const handleAccountClick = (account) => {
        const relevantMovements = [];
        ledgerEntries.forEach(asiento => {
            if (asiento.detalles) {
                asiento.detalles.forEach(detalle => {
                    if (detalle.cuentaId === account.id) {
                        relevantMovements.push({
                            ...detalle,
                            fecha: asiento.fecha,
                            concepto: asiento.concepto,
                            tipoReferencia: asiento.tipoReferencia,
                            referenciaId: asiento.referenciaId
                        });
                    }
                });
            }
        });
        setSelectedAccount(account);
        setSelectedEntries(relevantMovements);
    };

    // Grouping
    const grouped = {
        'INGRESO': [],
        'GASTO': []
    };

    let totalIngresos = 0;
    let totalGastos = 0;

    if (!loading) {
        accounts.forEach(acc => {
            let rawBalance = balances[acc.id] || 0;
            // Income (Acreedora) -> Balance < 0 is good (Credit > Debit)
            // Expense (Deudora) -> Balance > 0 is good (Debit > Credit)

            if (acc.tipo === 'INGRESO') {
                // Presentation: Positive
                let pres = rawBalance * -1;
                if (Math.abs(pres) > 0.01) {
                    grouped['INGRESO'].push({ ...acc, balance: pres });
                    totalIngresos += pres;
                }
            } else if (acc.tipo === 'GASTO') {
                let pres = rawBalance;
                if (Math.abs(pres) > 0.01) {
                    grouped['GASTO'].push({ ...acc, balance: pres });
                    totalGastos += pres;
                }
            }
        });
    }

    // Sort
    Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => a.numero.localeCompare(b.numero));
    });

    const utilidadNeta = totalIngresos - totalGastos;

    if (loading) return <div>Cargando Estado de Resultados...</div>;

    // Export Handlers
    const getExportData = () => {
        const data = [];
        data.push({ Numero: 'INGRESOS', Nombre: '', Balance: '' });
        grouped['INGRESO'].forEach(i => data.push({ Numero: i.numero, Nombre: i.nombre, Balance: i.balance }));
        data.push({ Numero: 'TOTAL INGRESOS', Nombre: '', Balance: totalIngresos });

        data.push({ Numero: 'GASTOS', Nombre: '', Balance: '' });
        grouped['GASTO'].forEach(g => data.push({ Numero: g.numero, Nombre: g.nombre, Balance: g.balance }));
        data.push({ Numero: 'TOTAL GASTOS', Nombre: '', Balance: totalGastos });

        data.push({ Numero: 'RESULTADO', Nombre: '', Balance: utilidadNeta });
        return data;
    };

    const handleExportCSV = () => {
        exportToCSV(getExportData(), `Estado_Resultados_${month + 1}_${year}`);
    };

    const handleExportExcel = () => {
        exportToExcel(getExportData(), `Estado_Resultados_${month + 1}_${year}`, 'Estado de Resultados', userData?.condominioNombre);
    };

    const handleExportPDF = () => {
        const columns = ['Número', 'Cuenta', 'Monto'];
        const rows = [];

        rows.push([{ content: 'INGRESOS', colSpan: 3, styles: { fillColor: [236, 253, 245], fontStyle: 'bold' } }]);
        grouped['INGRESO'].forEach(item => {
            rows.push([item.numero, item.nombre, `$${item.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);
        });
        rows.push([{ content: 'TOTAL INGRESOS', colSpan: 2 }, `$${totalIngresos.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);

        rows.push([{ content: 'GASTOS', colSpan: 3, styles: { fillColor: [255, 247, 237], fontStyle: 'bold' } }]);
        grouped['GASTO'].forEach(item => {
            rows.push([item.numero, item.nombre, `$${item.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);
        });
        rows.push([{ content: 'TOTAL GASTOS', colSpan: 2 }, `$${totalGastos.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);

        rows.push([{ content: 'UTILIDAD NETA', colSpan: 2, styles: { fontStyle: 'bold', fillColor: utilidadNeta >= 0 ? [240, 253, 244] : [254, 242, 242] } }, { content: `$${utilidadNeta.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);

        exportToPDF(
            `Estado de Resultados - ${month + 1}/${year}`,
            columns,
            rows,
            `Estado_Resultados_${month + 1}_${year}`,
            userData?.condominioNombre
        );
    };

    const renderRow = (acc) => (
        <tr
            key={acc.id}
            style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', transition: 'background 0.2s' }}
            onClick={() => handleAccountClick(acc)}
            className="report-row"
        >
            <td style={{ padding: '8px', color: '#666', width: '100px' }}>{acc.numero}</td>
            <td style={{ padding: '8px' }}>{acc.nombre}</td>
            <td style={{ padding: '8px', textAlign: 'right' }}>${new Intl.NumberFormat('es-MX').format(acc.balance.toFixed(2))}</td>
        </tr>
    );

    return (
        <div className="report-container">
            {selectedAccount && (
                <DetalleCuentaModal
                    account={selectedAccount}
                    entries={selectedEntries}
                    onClose={() => setSelectedAccount(null)}
                />
            )}

            {/* Header handled by Parent */}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px', maxWidth: '800px', margin: '0 auto 20px auto' }}>
                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>


            {loading ? <p>Cargando...</p> : (
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>

                    {/* INGRESOS */}
                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ background: '#ecfdf5', padding: '10px', borderRadius: '4px 4px 0 0', borderBottom: '2px solid #10b981', display: 'flex', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, color: '#065f46' }}>INGRESOS</h3>
                            <span style={{ fontWeight: 'bold', color: '#065f46' }}>${new Intl.NumberFormat('es-MX').format(totalIngresos.toFixed(2))}</span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                {grouped['INGRESO'].map(acc => renderRow(acc))}
                                {grouped['INGRESO'].length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '15px', color: '#999' }}>Sin movimientos</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* GASTOS */}
                    <div style={{ marginBottom: '30px' }}>
                        <div style={{ background: '#fff7ed', padding: '10px', borderRadius: '4px 4px 0 0', borderBottom: '2px solid #f97316', display: 'flex', justifyContent: 'space-between' }}>
                            <h3 style={{ margin: 0, color: '#9a3412' }}>GASTOS</h3>
                            <span style={{ fontWeight: 'bold', color: '#9a3412' }}>${new Intl.NumberFormat('es-MX').format(totalGastos.toFixed(2))}</span>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                {grouped['GASTO'].map(acc => renderRow(acc))}
                                {grouped['GASTO'].length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '15px', color: '#999' }}>Sin movimientos</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* NET INCOME */}
                    <div style={{ padding: '20px', background: utilidadNeta >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', textAlign: 'center', border: `2px solid ${utilidadNeta >= 0 ? '#22c55e' : '#ef4444'}` }}>
                        <h3 style={{ margin: '0 0 10px 0', color: '#444' }}>RESULTADO DEL EJERCICIO (Utilidad/Pérdida)</h3>
                        <span style={{ fontSize: '2em', fontWeight: 'bold', color: utilidadNeta >= 0 ? '#15803d' : '#b91c1c' }}>
                            ${new Intl.NumberFormat('es-MX').format(utilidadNeta.toFixed(2))}
                        </span>
                    </div>

                </div>
            )}
        </div>
    );
}

export default EstadoResultados;
